/**
 * Feature toggle store.
 *
 * Fetches the deployment-level feature manifest from GET /api/features
 * (public, no auth) and exposes an `isEnabled(feature)` helper for
 * conditional rendering throughout the app.
 *
 * The manifest also carries deployment-level RAG routing config:
 * - `ragProxyBase` — default ragConnector base URL (e.g. `/rag-proxy/<instance>`).
 *   Empty when the deployment hasn't set RAG_PROXY_BASE; consumers should
 *   fall back to `/rag-proxy` (Vite dev proxy / local docker default).
 * - `ragExternalAliases` — names of cross-env aliases registered via
 *   RAG_EXTERNAL_ALIAS_* env vars. Persona "Server URL" picker uses these
 *   to offer a `/rag-external/<alias>` choice. Target URLs are NOT exposed
 *   here (the endpoint is unauthenticated; URLs would leak topology).
 * - `defaultCurrency` — deployment-level default currency (ISO 4217 code,
 *   e.g. "USD", "MXN") used by `formatCents()` (`@/utils/currency`) when a
 *   record doesn't carry its own currency. Falls back to "USD".
 * - `defaultTimezone` — the deployment timezone's IANA name (SA-406). A naive
 *   datetime field value is wall time in this zone; read through
 *   `useDefaultTimezone()`. Falls back to "UTC".
 * - `vocabulary` — the deployment's vocabulary-pack pointer and the resolved
 *   record noun (E4, SA-385, spec D22): `{ pack, record: { singular, plural } }`.
 *   Record/Records with no pointer; read through `useRecordNoun()`.
 * - `companiesPage` — the manifest's `companies_page` (SA-1456 spec §7.2): an enabled
 *   module uses companies, so the admin area offers the Companies page. Strict: false
 *   until the manifest loads, and false when it cannot be read.
 */

import { create } from 'zustand'

export interface RecordNoun {
  singular: string
  plural: string
}

export interface DeploymentVocabulary {
  pack: string | null
  record: RecordNoun
}

export const DEFAULT_VOCABULARY: DeploymentVocabulary = {
  pack: null,
  record: { singular: 'Record', plural: 'Records' },
}

interface FeatureManifest {
  profile: string
  features: string[]
  version: string
  rag_proxy_base?: string | null
  rag_external_aliases?: string[]
  default_currency?: string
  default_timezone?: string
  vocabulary?: DeploymentVocabulary | null
  companies_page?: boolean
}

interface FeatureStore {
  features: Set<string>
  profile: string
  ragProxyBase: string | null
  ragExternalAliases: string[]
  defaultCurrency: string
  defaultTimezone: string
  vocabulary: DeploymentVocabulary
  companiesPage: boolean
  isLoaded: boolean
  isEnabled: (feature: string) => boolean
  loadFeatures: () => Promise<void>
}

/** A manifest from an older backend omits `vocabulary`; a partial one is
 * treated as absent rather than rendering an empty noun. */
function parseVocabulary(raw: FeatureManifest['vocabulary']): DeploymentVocabulary {
  if (!raw || !raw.record?.singular || !raw.record?.plural) return DEFAULT_VOCABULARY
  return {
    pack: raw.pack ?? null,
    record: { singular: raw.record.singular, plural: raw.record.plural },
  }
}

export const useFeatureStore = create<FeatureStore>((set, get) => ({
  features: new Set<string>(),
  profile: '',
  ragProxyBase: null,
  ragExternalAliases: [],
  defaultCurrency: 'USD',
  defaultTimezone: 'UTC',
  vocabulary: DEFAULT_VOCABULARY,
  companiesPage: false,
  isLoaded: false,

  isEnabled: (feature: string) => {
    const { isLoaded, features } = get()
    // Before features load, treat all as enabled (avoid flash of hidden content)
    if (!isLoaded) return true
    return features.has(feature)
  },

  loadFeatures: async () => {
    try {
      const res = await fetch('/api/features')
      if (!res.ok) {
        console.error('Failed to load feature manifest:', res.status)
        // On failure, mark as loaded with empty set — all features will appear disabled
        // except via the isEnabled fallback above, which returns true before isLoaded
        set({ isLoaded: true })
        return
      }
      const data: FeatureManifest = await res.json()
      set({
        features: new Set(data.features),
        profile: data.profile,
        ragProxyBase: data.rag_proxy_base ?? null,
        ragExternalAliases: data.rag_external_aliases ?? [],
        defaultCurrency: data.default_currency ?? 'USD',
        defaultTimezone: data.default_timezone ?? 'UTC',
        vocabulary: parseVocabulary(data.vocabulary),
        companiesPage: data.companies_page === true,
        isLoaded: true,
      })
    } catch (err) {
      console.error('Failed to load feature manifest:', err)
      set({ isLoaded: true })
    }
  },
}))
